// Chart-config schema v2: upgrade, date-range resolution and shared chart maths.
//
// Twin module of app/apps/tactical/chart_schema.py — the algorithms here must
// produce identical output to the Python so the interactive preview and the
// server-side PNG agree. Parity is pinned by twin unit tests using the same
// literals (tests/apps/tactical/test_chart_schema.py on the Python side).

export const CONFIG_SCHEMA_VERSION = 2;

export const DEFAULT_SOURCE_TEXT = 'Source: LSEG Datastream, Drummond Capital Partners';

export const TRAILING_PRESETS = ['1y', '3y', '5y', '10y', 'ytd'];

// NBER US recession months (peak → trough), month precision. Twin constant in
// chart_schema.py — keep byte-identical.
export const US_RECESSION_BANDS = [
  ['1969-12-01', '1970-11-30'],
  ['1973-11-01', '1975-03-31'],
  ['1980-01-01', '1980-07-31'],
  ['1981-07-01', '1982-11-30'],
  ['1990-07-01', '1991-03-31'],
  ['2001-03-01', '2001-11-30'],
  ['2007-12-01', '2009-06-30'],
  ['2020-02-01', '2020-04-30'],
];

export const DEFAULT_AXIS = {
  invert: false, log: false, min: null, max: null,
  decimals: null, pct_suffix: false, thousands: false,
};

export const DEFAULT_OPTIONS = {
  stacked: false,
  gridlines: false,
  legend: { show: true, position: 'bottom' },
  source_text: DEFAULT_SOURCE_TEXT,
  zero_line: false,
  last_value_labels: false,
};

export const DEFAULT_ANNOTATIONS = { bands: [], preset_bands: null, vlines: [], hlines: [] };

// ─── upgrade ────────────────────────────────────────────────────────────────

export function upgradeChartConfig(config) {
  if (!config || typeof config !== 'object') {
    throw new Error('chart config must be an object');
  }
  const version = config.schema_version;
  if (version !== 1 && version !== CONFIG_SCHEMA_VERSION) {
    throw new Error(`unsupported chart config schema_version ${version}`);
  }

  const out = JSON.parse(JSON.stringify(config));
  out.schema_version = CONFIG_SCHEMA_VERSION;
  if (!out.chart_mode) out.chart_mode = 'timeseries';

  (out.series || []).forEach(s => {
    if (s.type === undefined) s.type = 'line';
    if (s.line_width === undefined) s.line_width = 1.5;
    if (s.line_style === undefined) s.line_style = 'solid';
    if (s.band === undefined) s.band = null;
    if (s.resample === undefined) s.resample = null;
    if (s.anchor === undefined) s.anchor = null;
  });

  const axisConfig = out.axis_config || {};
  ['y', 'y1'].forEach(axis => {
    axisConfig[axis] = { ...DEFAULT_AXIS, ...(axisConfig[axis] || {}) };
  });
  out.axis_config = axisConfig;

  let dr = out.date_range || {};
  if (version === 1 || dr.mode === undefined) {
    const { start = null, end = null } = dr;
    dr = { mode: (start || end) ? 'fixed' : 'all', trailing: null, start, end };
  }
  if (dr.trailing === undefined) dr.trailing = null;
  if (dr.start === undefined) dr.start = null;
  if (dr.end === undefined) dr.end = null;
  const zoom = dr.zoom || {};
  dr.zoom = {
    start_pct: zoom.start_pct !== undefined ? zoom.start_pct : 0,
    end_pct: zoom.end_pct !== undefined ? zoom.end_pct : 100,
  };
  out.date_range = dr;

  const options = JSON.parse(JSON.stringify(DEFAULT_OPTIONS));
  Object.entries(out.options || {}).forEach(([key, value]) => {
    if (key === 'legend' && value && typeof value === 'object') {
      options.legend = { ...options.legend, ...value };
    } else {
      options[key] = value;
    }
  });
  out.options = options;

  out.annotations = { ...JSON.parse(JSON.stringify(DEFAULT_ANNOTATIONS)), ...(out.annotations || {}) };
  if (out.histogram === undefined) out.histogram = null;
  if (out.scatter === undefined) out.scatter = null;
  if (out.seasonal === undefined) out.seasonal = null;
  if (out.derived === undefined) out.derived = [];
  if (out.footnote === undefined) out.footnote = null;
  return out;
}

// ─── date-range resolution ──────────────────────────────────────────────────

function shiftYears(dateStr, years) {
  // Subtract N whole years from an ISO date string, clamping 29 Feb → 28 Feb.
  // String arithmetic only — identical to the Python twin.
  const y = parseInt(dateStr.slice(0, 4), 10);
  const m = dateStr.slice(5, 7);
  let d = dateStr.slice(8, 10);
  if (m === '02' && d === '29') d = '28';
  return `${String(y - years).padStart(4, '0')}-${m}-${d}`;
}

export function resolveDateRange(dateRange, dates) {
  // Returns [lo, hi) slice bounds into `dates`. Trailing modes anchor on the
  // LAST AVAILABLE DATA DATE so saved charts roll forward; zoom percentages
  // apply within the resolved window (floor/ceil — same as the Python twin).
  const n = dates.length;
  if (n === 0) return [0, 0];
  const dr = dateRange || {};
  const mode = dr.mode || 'all';

  let start = null, end = null;
  if (mode === 'fixed') {
    start = dr.start || null;
    end = dr.end || null;
  } else if (mode === 'trailing') {
    const trailing = dr.trailing;
    const anchor = dates[n - 1];
    if (trailing === 'ytd') {
      start = `${anchor.slice(0, 4)}-01-01`;
    } else if (TRAILING_PRESETS.includes(trailing)) {
      start = shiftYears(anchor, parseInt(trailing, 10));
    } else {
      throw new Error(`unknown trailing preset ${trailing}`);
    }
  } else if (mode !== 'all') {
    throw new Error(`unknown date_range mode ${mode}`);
  }

  let lo = 0, hi = n;
  if (start || end) {
    const idx = [];
    for (let i = 0; i < n; i++) {
      if ((start === null || dates[i] >= start) && (end === null || dates[i] <= end)) idx.push(i);
    }
    if (idx.length) { lo = idx[0]; hi = idx[idx.length - 1] + 1; }
  }

  const zoom = dr.zoom || {};
  const startPct = zoom.start_pct !== undefined ? zoom.start_pct : 0;
  const endPct = zoom.end_pct !== undefined ? zoom.end_pct : 100;
  if (startPct > 0 || endPct < 100) {
    const length = hi - lo;
    const zLo = lo + Math.floor((startPct / 100) * length);
    const zHi = lo + Math.ceil((endPct / 100) * length);
    lo = zLo;
    hi = Math.max(zLo, zHi);
  }
  return [lo, hi];
}

// ─── histogram ──────────────────────────────────────────────────────────────

export function computeHistogram(values, bins) {
  // Equal-width bins over [min, max] of the finite values; top edge inclusive.
  const finite = values.filter(v => v != null && typeof v === 'number' && Number.isFinite(v));
  if (!finite.length) {
    throw new Error('histogram has no data in the selected date range');
  }
  const vmin = Math.min(...finite);
  const vmax = Math.max(...finite);
  if (vmin === vmax) {
    return { edges: [vmin - 0.5, vmax + 0.5], counts: [finite.length] };
  }
  const width = (vmax - vmin) / bins;
  const counts = new Array(bins).fill(0);
  finite.forEach(v => {
    counts[Math.min(bins - 1, Math.floor((v - vmin) / width))] += 1;
  });
  const edges = [];
  for (let i = 0; i < bins; i++) edges.push(vmin + i * width);
  edges.push(vmax);
  return { edges, counts };
}

// ─── OLS trend line ─────────────────────────────────────────────────────────

export function olsFit(xs, ys) {
  const n = xs.length;
  if (n < 2 || n !== ys.length) {
    throw new Error('trend line needs at least 2 paired points');
  }
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;
  let sxx = 0, sxy = 0;
  for (let i = 0; i < n; i++) {
    sxx += (xs[i] - meanX) ** 2;
    sxy += (xs[i] - meanX) * (ys[i] - meanY);
  }
  if (sxx === 0) throw new Error('trend line undefined: x values are constant');
  const slope = sxy / sxx;
  return [slope, meanY - slope * meanX];
}

// ─── derived expression series ──────────────────────────────────────────────

export function derivedKey(d) {
  return `=${d.id}`;
}

export function computeDerived(d, a, b) {
  // b is unused for op 'scale'. Division keeps raw JS semantics (x/0 → ±Inf,
  // 0/0 → NaN) so gaps match the server renderer's _clean handling.
  const n = a.length;
  const out = new Array(n).fill(null);
  if (d.op === 'ratio') {
    for (let i = 0; i < n; i++) {
      if (a[i] != null && b[i] != null) out[i] = a[i] / b[i];
    }
  } else if (d.op === 'spread') {
    for (let i = 0; i < n; i++) {
      if (a[i] != null && b[i] != null) out[i] = a[i] - b[i];
    }
  } else if (d.op === 'scale') {
    const k = d.k !== undefined && d.k !== null ? d.k : 1;
    for (let i = 0; i < n; i++) {
      if (a[i] != null) out[i] = a[i] * k;
    }
  } else if (d.op === 'corr') {
    const window = d.window || 60;
    for (let i = 0; i < n; i++) {
      if (i < window - 1) continue;
      const xs = [], ys = [];
      for (let j = i - window + 1; j <= i; j++) {
        if (a[j] != null && b[j] != null) { xs.push(a[j]); ys.push(b[j]); }
      }
      if (xs.length < 2) continue;
      const mx = xs.reduce((p, q) => p + q, 0) / xs.length;
      const my = ys.reduce((p, q) => p + q, 0) / ys.length;
      let sxx = 0, syy = 0, sxy = 0;
      for (let j = 0; j < xs.length; j++) {
        sxx += (xs[j] - mx) ** 2;
        syy += (ys[j] - my) ** 2;
        sxy += (xs[j] - mx) * (ys[j] - my);
      }
      if (sxx === 0 || syy === 0) continue;
      out[i] = sxy / Math.sqrt(sxx * syy);
    }
  } else {
    throw new Error(`unknown derived op ${d.op}`);
  }
  return out;
}

// ─── frequency resampling ───────────────────────────────────────────────────

function jdn(y, m, d) {
  // Gregorian date → Julian day number; jdn % 7 === 0 on Mondays.
  const a = Math.floor((14 - m) / 12);
  const yy = y + 4800 - a;
  const mm = m + 12 * a - 3;
  return d + Math.floor((153 * mm + 2) / 5) + 365 * yy + Math.floor(yy / 4)
    - Math.floor(yy / 100) + Math.floor(yy / 400) - 32045;
}

function periodKey(dateStr, freq) {
  if (freq === 'monthly') return dateStr.slice(0, 7);
  // Weekly: group Mon–Sun by the JDN of the week's Monday (Python twin).
  const j = jdn(parseInt(dateStr.slice(0, 4), 10),
                parseInt(dateStr.slice(5, 7), 10),
                parseInt(dateStr.slice(8, 10), 10));
  return `W${j - (j % 7)}`;
}

export function resampleSeries(dates, values, freq) {
  // Downsample to weekly/monthly: one point per period, stamped on the
  // period's last available date, holding the period's last non-null value.
  if (freq !== 'weekly' && freq !== 'monthly') {
    throw new Error(`unknown resample freq ${freq}`);
  }
  const outDates = [], outValues = [];
  let currentKey = null;
  for (let i = 0; i < dates.length; i++) {
    const key = periodKey(dates[i], freq);
    if (key !== currentKey) {
      outDates.push(dates[i]);
      outValues.push(values[i]);
      currentKey = key;
    } else {
      outDates[outDates.length - 1] = dates[i];
      if (values[i] != null) outValues[outValues.length - 1] = values[i];
    }
  }
  return [outDates, outValues];
}

// ─── seasonal split ─────────────────────────────────────────────────────────

// Cumulative days before each month in a non-leap year; 29 Feb shares
// position 59 with 1 Mar. Twin constant in chart_schema.py.
export const MONTH_CUM_DAYS = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];

export function dayOfYearPosition(dateStr) {
  const m = parseInt(dateStr.slice(5, 7), 10);
  let d = parseInt(dateStr.slice(8, 10), 10);
  if (m === 2 && d === 29) d = 28;
  return MONTH_CUM_DAYS[m - 1] + (d - 1);
}

export function seasonalSplit(dates, values, years) {
  // Per-calendar-year traces over the last `years` years, anchored on the
  // last data date's year; null values dropped.
  if (!dates.length) throw new Error('seasonal chart has no data');
  const lastYear = parseInt(dates[dates.length - 1].slice(0, 4), 10);
  const firstYear = lastYear - years + 1;
  const byYear = new Map();
  for (let i = 0; i < dates.length; i++) {
    if (values[i] == null) continue;
    const y = parseInt(dates[i].slice(0, 4), 10);
    if (y < firstYear) continue;
    if (!byYear.has(y)) byYear.set(y, []);
    byYear.get(y).push([dayOfYearPosition(dates[i]), values[i]]);
  }
  if (!byYear.size) throw new Error('seasonal chart has no data in the selected years');
  return [...byYear.keys()].sort((a, b) => a - b)
    .map(year => ({ year, points: byYear.get(year) }));
}
